import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Package, AlertTriangle, ArrowUpRight, WifiOff, Plus, ScanLine, CalendarDays } from "lucide-react";
import AnalyticsView from "../components/AnalyticsView";
import OnboardingWizard from "../components/OnboardingWizard";
import { fmtMoney, fmtInt } from "../lib/format";

function isUsableSummary(d) {
  return d && typeof d === "object" && Array.isArray(d.low_stock);
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [offline, setOffline] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      if (!isUsableSummary(data)) {
        setOffline(true);
        return;
      }
      setOffline(false);
      setSummary(data);
    } catch (_e) {
      setOffline(true);
    }
  };
  useEffect(() => { load(); }, []);

  // Show the onboarding wizard once per admin with zero products and no
  // prior completion flag in localStorage.
  useEffect(() => {
    if (!user?.user_id || !isAdmin || !summary) return;
    if ((summary.total_products || 0) > 0) return;
    let done = "";
    try { done = localStorage.getItem(`vv_onboarding_done:${user.user_id}`) || ""; } catch { /* ignore */ }
    if (!done) setShowOnboarding(true);
  }, [user?.user_id, isAdmin, summary]);

  if (offline && !summary) {
    return (
      <div className="brutal-card p-8 max-w-xl" data-testid="dashboard-offline">
        <div className="flex items-center gap-3 mb-3">
          <WifiOff size={20} />
          <div className="font-mono text-xs uppercase tracking-widest">Offline</div>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black uppercase mb-3">Dashboard needs Wi-Fi</h1>
        <p className="font-mono text-sm text-[#4A4A4A] mb-6">
          Totals &amp; low-stock alerts run on the server. Your products, scanner, and event sales still work offline — head there to keep selling.
        </p>
        <div className="flex gap-3 flex-wrap">
          <Link to="/products" className="brutal-btn brutal-btn-secondary">Inventory</Link>
          <Link to="/scanner" className="brutal-btn">Scan &amp; Sell</Link>
          <button onClick={load} className="brutal-btn brutal-btn-secondary" data-testid="dashboard-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (!summary) return <div className="font-mono uppercase tracking-widest text-sm">Loading…</div>;

  const lowStock = summary.low_stock || [];

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="dashboard-page">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-[#4A4A4A]">Control Room</div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">Dashboard</h1>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/scanner" className="brutal-btn brutal-btn-secondary !py-2 !px-3">
              <ScanLine size={14}/><span className="ml-2">Scan</span>
            </Link>
            <Link to="/events" className="brutal-btn brutal-btn-secondary !py-2 !px-3 hidden sm:inline-flex">
              <CalendarDays size={14}/><span className="ml-2">Events</span>
            </Link>
            <Link to="/products/new" className="brutal-btn !py-2 !px-3" data-testid="dashboard-new-item-btn">
              <Plus size={14}/><span className="ml-2">New item</span>
            </Link>
          </div>
        )}
      </div>

      {/* Low stock alert (compact) */}
      {lowStock.length > 0 && (() => {
        const oos = lowStock.filter(x => x.stock === 0).length;
        const low = lowStock.length - oos;
        const parts = [];
        if (oos > 0) parts.push(`${fmtInt(oos)} OUT OF STOCK`);
        if (low > 0) parts.push(`${fmtInt(low)} low on stock`);
        return (
          <div className="alert-bar flex items-center gap-3" data-testid="low-stock-alert">
            <AlertTriangle size={18}/> {parts.join(" · ")}
          </div>
        );
      })()}

      {/* Inventory snapshot — compact strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <SnapshotCard label="Total Products" value={fmtInt(summary.total_products)} testid="snap-products" />
        <SnapshotCard label="Items in Stock" value={fmtInt(summary.total_items)} testid="snap-items" />
        {isAdmin && <SnapshotCard label="Inventory Cost" value={fmtMoney(summary.total_cost)} testid="snap-cost" />}
        {isAdmin && <SnapshotCard label="Inventory Value" value={fmtMoney(summary.total_value)} testid="snap-value" />}
        {!isAdmin && <SnapshotCard label="Out of Stock" value={fmtInt(summary.out_of_stock)} testid="snap-oos" />}
      </div>

      {/* Date-range analytics — the new heart of the dashboard */}
      {isAdmin && <AnalyticsView title="Sales analytics" defaultRange="30d" />}

      {/* Low stock list — bottom because most-of-the-time empty */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="brutal-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-xl font-black uppercase">Low stock</h2>
            <span className="brutal-badge">{lowStock.length}</span>
          </div>
          {lowStock.length === 0 ? (
            <div className="font-mono text-sm text-[#4A4A4A] uppercase tracking-wider py-6 text-center">All stocked up.</div>
          ) : (
            <ul className="divide-y-2 divide-black" data-testid="low-stock-list">
              {lowStock.slice(0, 12).map((it) => (
                <li key={it.variant_id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-heading font-bold truncate">{it.product_name}</div>
                    <div className="font-mono text-xs text-[#4A4A4A] uppercase tracking-widest truncate">{it.sku}</div>
                  </div>
                  <Link to={`/products/${it.product_id}`} className={`brutal-badge flex-shrink-0 ${it.stock === 0 ? "!bg-[#C53030] !text-white" : "!bg-[#FFB100]"}`}>
                    {it.stock === 0 ? "OUT" : `${fmtInt(it.stock)} left`} <ArrowUpRight size={12} className="ml-1"/>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="brutal-card p-5">
          <div className="font-heading text-xl font-black uppercase mb-3">Pro tip</div>
          <div className="shipping-tag">
            <div className="font-mono text-[10px] uppercase tracking-widest">Booth setup</div>
            <div className="mt-1 font-heading text-lg font-black uppercase">
              Print barcodes &amp; tape them to your bins.
            </div>
            <div className="mt-2 font-mono text-xs text-[#4A4A4A]">
              Open a product, hit &quot;Print Tag&quot;, scan on arrival.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Link to="/products" className="brutal-btn brutal-btn-secondary !py-2 text-xs">Inventory</Link>
            <Link to="/scanner" className="brutal-btn !py-2 text-xs">Scan</Link>
          </div>
        </div>
      </div>
      {showOnboarding && (
        <OnboardingWizard user={user} onClose={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}

function SnapshotCard({ label, value, testid }) {
  return (
    <div className="brutal-card p-3 sm:p-4" data-testid={testid}>
      <div className="font-mono text-[9px] sm:text-[10px] uppercase tracking-widest text-[#4A4A4A] flex items-center gap-1">
        <Package size={10}/> {label}
      </div>
      <div className="mt-1 font-heading text-xl sm:text-2xl font-black">{value}</div>
    </div>
  );
}
